import { supabase } from '../lib/supabase';

/**
 * Offline Queue Manager
 * Handles queueing of operations when offline and syncing when connection is restored
 */
class OfflineQueueManager {
  constructor() {
    this.queue = this.loadQueue();
    this.isProcessing = false;
    this.maxRetries = 3;
    this.retryDelay = 2000; // 2 seconds
  }

  /**
   * Load queue from localStorage
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem('supabase_offline_queue');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load offline queue:', error);
      return [];
    }
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem('supabase_offline_queue', JSON.stringify(this.queue));
    } catch (error) {
      console.error('Failed to save offline queue:', error);
    }
  }

  /**
   * Add operation to queue
   */
  enqueue(operation) {
    const queueItem = {
      id: `${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`,
      operation,
      timestamp: Date.now(),
      retries: 0,
      status: 'pending'
    };

    this.queue?.push(queueItem);
    this.saveQueue();
    return queueItem?.id;
  }

  /**
   * Process queue when online
   */
  async processQueue() {
    if (this.isProcessing || this.queue?.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      const pendingItems = this.queue?.filter(item => item?.status === 'pending' || item?.status === 'retrying');

      for (const item of pendingItems) {
        try {
          await this.executeOperation(item?.operation);
          
          // Remove successful item from queue
          this.queue = this.queue?.filter(q => q?.id !== item?.id);
          this.saveQueue();
        } catch (error) {
          console.error('Failed to process queue item:', error);
          
          // Increment retry count
          item.retries += 1;
          
          if (item?.retries >= this.maxRetries) {
            item.status = 'failed';
            item.error = error?.message;
          } else {
            item.status = 'retrying';
            // Wait before next retry
            await new Promise(resolve => setTimeout(resolve, this.retryDelay * item.retries));
          }
          
          this.saveQueue();
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Execute a queued operation
   */
  async executeOperation(operation) {
    const { type, table, data, id, method } = operation;

    switch (method) {
      case 'insert':
        const { error: insertError } = await supabase?.from(table)?.insert(data);
        if (insertError) throw insertError;
        break;

      case 'update':
        const { error: updateError } = await supabase?.from(table)?.update(data)?.eq('id', id);
        if (updateError) throw updateError;
        break;

      case 'delete':
        const { error: deleteError } = await supabase?.from(table)?.delete()?.eq('id', id);
        if (deleteError) throw deleteError;
        break;

      default:
        throw new Error(`Unknown operation method: ${method}`);
    }
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      total: this.queue?.length,
      pending: this.queue?.filter(item => item?.status === 'pending')?.length,
      retrying: this.queue?.filter(item => item?.status === 'retrying')?.length,
      failed: this.queue?.filter(item => item?.status === 'failed')?.length
    };
  }

  /**
   * Clear failed items
   */
  clearFailed() {
    this.queue = this.queue?.filter(item => item?.status !== 'failed');
    this.saveQueue();
  }

  /**
   * Retry failed items
   */
  retryFailed() {
    this.queue?.forEach(item => {
      if (item?.status === 'failed') {
        item.status = 'pending';
        item.retries = 0;
        delete item?.error;
      }
    });
    this.saveQueue();
    this.processQueue();
  }

  /**
   * Clear all items
   */
  clearAll() {
    this.queue = [];
    this.saveQueue();
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueueManager();

/**
 * Enhanced service wrapper with offline support
 */
export const withOfflineSupport = (serviceFunction) => {
  return async (...args) => {
    try {
      // Try to execute the operation
      const result = await serviceFunction(...args);
      return result;
    } catch (error) {
      // Check if it's a network error
      if (error?.message?.includes('fetch') || error?.message?.includes('network') || !navigator.onLine) {
        // Queue the operation for later
        const operation = {
          method: serviceFunction?.name,
          args,
          timestamp: Date.now()
        };
        
        offlineQueue?.enqueue(operation);
        
        return {
          queued: true,
          message: 'Operation queued for sync when connection is restored'
        };
      }
      
      // Re-throw non-network errors
      throw error;
    }
  };
};